/**
 * The screen the person is looking at, set by the shell whenever it changes.
 *
 * Module state rather than a parameter on every call: the alternative is threading it
 * through every component down to every fetch, which is how it ends up missing from exactly
 * the call somebody later wants to trace.
 */
let currentScreen = null;
export const setCurrentScreen = s => { currentScreen = s; };

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch('/api' + path, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        // Where the person was when they did it. Sent on every call so every audit entry
        // knows which screen it came from without a single route having to pass it along.
        ...(currentScreen ? { 'x-epms-screen': currentScreen } : {}),
        ...(options.headers || {})
      },
      ...options,
      body: options.body ? JSON.stringify(options.body) : undefined
    });
  } catch (cause) {
    // The API is not answering at all: restarting, stopped, or the network is out. This is
    // not the same as being refused, and a screen that treats it as one tells somebody
    // their password is wrong when it is not.
    const err = new Error('unreachable');
    err.unreachable = true;
    err.cause = cause;
    throw err;
  }
  let data = null;
  try { data = await res.json(); } catch { /* empty body is fine */ }
  if (!res.ok) {
    const err = new Error((data && data.error) || 'request_failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  health: () => request('/health'),
  me: () => request('/auth/me'),
  signIn: (username, password) => request('/auth/sign-in', { method: 'POST', body: { username, password } }),
  twoFactor: payload => request('/auth/two-factor', { method: 'POST', body: payload }),
  signOut: () => request('/auth/sign-out', { method: 'POST' }),
  businesses: () => request('/auth/businesses'),
  enterBusiness: slug => request(`/businesses/${slug}/enter`, { method: 'POST' }),
  business: slug => request(`/businesses/${slug}`),
  audit: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request('/audit' + (q ? '?' + q : ''));
  },
  auditAttention: (days = 7) => request(`/audit/attention?days=${days}`),
  auditPeople: () => request('/audit/people'),
  auditPerson: userId => request(`/audit/person/${userId}`),
  /** Fire and forget: a failed screen record must never interrupt somebody selling. */
  auditView: (screen, businessId) =>
    request('/audit/view', { method: 'POST', body: { screen, businessId } }).catch(() => {}),
  changePassword: (currentPassword, newPassword) =>
    request('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),

  // Accounts. Every one of these returns the updated user, so the screen never guesses.
  users: {
    list: () => request('/users'),
    roles: () => request('/users/roles'),
    create: body => request('/users', { method: 'POST', body }),
    update: (id, body) => request(`/users/${id}`, { method: 'PATCH', body }),
    suspend: (id, reason) => request(`/users/${id}/suspend`, { method: 'POST', body: { reason } }),
    restore: id => request(`/users/${id}/restore`, { method: 'POST' }),
    resetPassword: id => request(`/users/${id}/reset-password`, { method: 'POST' }),
    resetMfa: id => request(`/users/${id}/reset-mfa`, { method: 'POST' }),
    unlock: id => request(`/users/${id}/unlock`, { method: 'POST' }),
    signOutEverywhere: id => request(`/users/${id}/sign-out`, { method: 'POST' })
  },

  // Above the businesses: the console the super admin and the admin work from.
  capabilities: () => request('/capabilities'),
  dashboard: () => request('/dashboard'),
  inbox: (unread = false) => request('/inbox' + (unread ? '?unread=1' : '')),
  unreadCount: () => request('/inbox/unread'),
  delivery: id => request(`/inbox/${id}`),
  sentReports: () => request('/sent'),

  // The tills, and the printer wired to each. Super admin only: which machines exist is
  // the shape of the system, not the running of it.
  tills: {
    list: () => request('/tills'),
    transports: () => request('/tills/transports'),
    create: body => request('/tills', { method: 'POST', body }),
    pair: id => request(`/tills/${id}/pair`, { method: 'POST' }),
    revoke: id => request(`/tills/${id}/revoke`, { method: 'POST' }),
    saveSettings: (id, settings) => request(`/tills/${id}/settings`, { method: 'PUT', body: settings }),
    gaps: id => request(`/tills/${id}/gaps`),
    testPrint: (id, settings) => request(`/tills/${id}/test-print`, { method: 'POST', body: settings }),
    openDrawer: (id, settings) => request(`/tills/${id}/open-drawer`, { method: 'POST', body: settings }),
    printSale: (id, saleId, settings) =>
      request(`/tills/${id}/print/${saleId}`, { method: 'POST', body: settings })
  },

  // Everything below is scoped to one business by slug.
  b: slug => ({
    catalogue: () => request(`/b/${slug}/catalogue`),
    staff: serviceId => request(`/b/${slug}/staff` + (serviceId ? `?serviceId=${serviceId}` : '')),
    setStaffCategories: (id, categoryIds) => request(`/b/${slug}/staff/${id}/categories`, { method: 'PUT', body: { categoryIds } }),
    customers: q => request(`/b/${slug}/customers` + (q ? `?q=${encodeURIComponent(q)}` : '')),
    createCustomer: body => request(`/b/${slug}/customers`, { method: 'POST', body }),
    nextRef: () => request(`/b/${slug}/next-ref`),
    createSale: body => request(`/b/${slug}/sales`, { method: 'POST', body }),
    sales: (params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/b/${slug}/sales` + (q ? '?' + q : ''));
    },
    sale: id => request(`/b/${slug}/sales/${id}`),
    voidSale: (id, reason) => request(`/b/${slug}/sales/${id}/void`, { method: 'POST', body: { reason } }),
    cashSession: () => request(`/b/${slug}/cash-session`),
    openSession: openingFloat => request(`/b/${slug}/cash-session/open`, { method: 'POST', body: { openingFloat } }),
    closeSession: body => request(`/b/${slug}/cash-session/close`, { method: 'POST', body }),
    appointments: date => request(`/b/${slug}/appointments?date=${date}`),
    createAppointment: body => request(`/b/${slug}/appointments`, { method: 'POST', body }),
    setAppointmentStatus: (id, status) => request(`/b/${slug}/appointments/${id}`, { method: 'PATCH', body: { status } }),
    checkIn: id => request(`/b/${slug}/appointments/${id}/check-in`, { method: 'POST' }),
    drafts: () => request(`/b/${slug}/drafts`),
    holdDraft: body => request(`/b/${slug}/drafts`, { method: 'POST', body }),
    dropDraft: (id, reason) => request(`/b/${slug}/drafts/${id}?reason=${reason}`, { method: 'DELETE' }),
    createService: body => request(`/b/${slug}/services`, { method: 'POST', body }),
    updateService: (id, body) => request(`/b/${slug}/services/${id}`, { method: 'PATCH', body }),
    importServices: rows => request(`/b/${slug}/services/import`, { method: 'POST', body: { rows } }),
    updateStaff: (id, body) => request(`/b/${slug}/staff/${id}`, { method: 'PATCH', body }),
    report: (name, params = {}) => {
      const q = new URLSearchParams(params).toString();
      return request(`/b/${slug}/reports/${name}` + (q ? '?' + q : ''));
    },

    // Adding and retiring what the business sells, and who performs it. Nothing here
    // deletes: retiring is a state with a date and a reason, because sale lines months old
    // still point at these rows.
    categories: (all = false) => request(`/b/${slug}/categories` + (all ? '?all=1' : '')),
    addCategory: name => request(`/b/${slug}/categories`, { method: 'POST', body: { name } }),
    renameCategory: (id, name) => request(`/b/${slug}/categories/${id}`, { method: 'PATCH', body: { name } }),
    retireCategory: (id, reason) => request(`/b/${slug}/categories/${id}/retire`, { method: 'POST', body: { reason } }),
    restoreCategory: id => request(`/b/${slug}/categories/${id}/restore`, { method: 'POST' }),

    catalogueServices: (all = false) => request(`/b/${slug}/services` + (all ? '?all=1' : '')),
    addService: body => request(`/b/${slug}/services`, { method: 'POST', body }),
    retireService: (id, reason) => request(`/b/${slug}/services/${id}/retire`, { method: 'POST', body: { reason } }),
    restoreService: id => request(`/b/${slug}/services/${id}/restore`, { method: 'POST' }),

    staffRecords: (all = false) => request(`/b/${slug}/staff-records` + (all ? '?all=1' : '')),
    addStaff: body => request(`/b/${slug}/staff-records`, { method: 'POST', body }),
    retireStaff: (id, reason) => request(`/b/${slug}/staff-records/${id}/retire`, { method: 'POST', body: { reason } }),
    returnStaff: id => request(`/b/${slug}/staff-records/${id}/return`, { method: 'POST' }),

    // A rate for one person on one service, which beats the service's rule and their default.
    staffRates: id => request(`/b/${slug}/staff-records/${id}/rates`),
    setStaffRate: (id, serviceId, body) =>
      request(`/b/${slug}/staff-records/${id}/rates/${serviceId}`, { method: 'PUT', body }),
    clearStaffRate: (id, serviceId) =>
      request(`/b/${slug}/staff-records/${id}/rates/${serviceId}`, { method: 'DELETE' }),

    // Build a report, read it, then send it up to the admins.
    buildReport: kind => request(`/b/${slug}/report/${kind}`),
    sendReport: (kind, body) => request(`/b/${slug}/report/${kind}/send`, { method: 'POST', body })
  })
};
